import React, { useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Alert, Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import MetaTags from "react-meta-tags";
import AuthLayout from "../../components/authenticate/AuthLayout";

const AdminLogin = () => {
  const history = useHistory();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setErrorMsg("Please enter both admin username/email and password.");
      return;
    }

    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    const backendUrl = process.env.REACT_APP_DEFAULTPATH || "/";
    const endpoint = backendUrl.endsWith("/") ? `${backendUrl}api/admin/login` : `${backendUrl}/api/admin/login`;

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
          password: password,
        }),
      });

      const data = await response.json();

      if (response.ok && data.status === "success") {
        localStorage.setItem("adminToken", data.token);
        localStorage.setItem("adminUser", JSON.stringify(data.admin));
        setSuccessMsg("Admin authentication successful! Redirecting to Key Vault...");
        setTimeout(() => {
          history.push("/admin/api-keys");
        }, 800);
      } else {
        setErrorMsg(data.message || "Invalid admin credentials. Access Denied.");
      }
    } catch (err) {
      console.error("Admin login error:", err);
      setErrorMsg("Unable to connect to Glogix Admin Service. Ensure server is running.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <MetaTags>
        <title>Admin Portal | Glogix API Vault</title>
      </MetaTags>

      <AuthLayout
        footer={
          <Link to="/login" className="d-inline-flex align-items-center">
            <FeatherIcon icon="arrow-left" size={14} className="me-1" />
            Back to User Login
          </Link>
        }
      >
        <div className="gx-auth-card">
          <div className="gx-auth-title">
            <div className="gx-status-icon" style={{ background: "rgba(249, 115, 22, 0.12)", color: "#fb923c", borderColor: "rgba(249, 115, 22, 0.35)" }}>
              <FeatherIcon icon="shield" size={26} />
            </div>
            <h4>Admin Sign In</h4>
            <p>Restricted gateway for API keys &amp; system vaults.</p>
          </div>

          {errorMsg && (
            <Alert color="danger" className="d-flex align-items-center py-2">
              <FeatherIcon icon="alert-circle" size={16} className="me-2 flex-shrink-0" />
              <div>{errorMsg}</div>
            </Alert>
          )}

          {successMsg && (
            <Alert color="success" className="d-flex align-items-center py-2">
              <FeatherIcon icon="check-circle" size={16} className="me-2 flex-shrink-0" />
              <div>{successMsg}</div>
            </Alert>
          )}

          <form onSubmit={handleAdminLogin}>
            <div className="mb-3">
              <label className="form-label">Admin Identifier / Email</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. admin@guardlogix.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="mb-4">
              <label className="form-label">Master Password</label>
              <div className="gx-password">
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-control"
                  placeholder="••••••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="gx-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <FeatherIcon icon={showPassword ? "eye-off" : "eye"} size={16} />
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} className="gx-btn-admin">
              {loading ? (
                <>
                  <Spinner size="sm" />
                  Authenticating...
                </>
              ) : (
                <>
                  <FeatherIcon icon="lock" size={15} />
                  Unlock Admin Console
                </>
              )}
            </button>
          </form>
        </div>
      </AuthLayout>
    </React.Fragment>
  );
};

export default AdminLogin;
